import React, { memo, forwardRef, useState } from 'react';
import useEnlog from 'app/hook/common/useEnlog';
import { numberWithCommas } from 'app/libs/number';
import classNames from 'classnames/bind';
import styles from './InputText.module.scss';

const cx = classNames.bind(styles);

const InputText = forwardRef((props, ref) => {
	const {
		type,
		id,
		name,
		label = '',
		subLabel = '',
		value = '',
		placeholder = '',
		maxLength = undefined,
		readOnly = false,
		placeholderEffect = 'normal',
		onChange = undefined,
		onFocus = undefined,
		onBlur = undefined,
		onKeyPress = undefined,
		onKeyDown = undefined,
		useLabel = false,
		useLabelBlind = false,
		useSubLabel = false,
		required = false,
		validation = false,
		validationText = '',
		validationTextAuto = '',
		useBtn = false,
		useBtnClear = false,
		onClick = undefined,
		btnClass = '',
		btnName = '',
		btnDisabled = false,
		useUnit = false,
		unitName = '',
		classArray = [],
		style = null,
		inputDesign = 'input',
		inputDesignClass = '',
		testId = 'input',
		disabled = false,
		inputMode = undefined,
		autoComplete = 'on',
		labelStyle = undefined,
		useDark = false,
		unitClass = '',
		useCommaFormat = false,
		valueClass = '',
		useBtnEmptyDisabled = false,
	} = props;
	const [isFocused, setIsFocused] = useState(false);

	const { propsDataset } = useEnlog();
	const dataSet = propsDataset(props);

	const design = `inner_${inputDesign}`;

	const handleClear = (e) => {
		const newEvent = {
			...e,
			target: { ...e.target, value: '' },
		};

		if (onChange) {
			onChange(newEvent);
		}
	};

	const onPlaceHolderFocus = (tg) => {
		document.getElementById(tg).focus(); // pointer-events:none 대응 코드
	};

	const noFocus = (e) => {
		e.target.blur();
	};

	const getInputType = () => {
		if (type === 'customNumber') {
			return 'text';
		}
		return type;
	};

	const trimByInputType = (e) => {
		if (type === 'customNumber') {
			const { target } = e;
			const result = target.value.toString().replace(/[^0-9]/g, '');
			target.value = result;
		}
	};

	const onChangeWrapper = (e) => {
		if (maxLength) {
			if (e.target.value.length > maxLength) {
				return;
			}
		}

		trimByInputType(e);

		if (onChange) {
			onChange(e);
		}
	};

	const internalOnFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};

	const internalOnBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};

	return (
		<div
			className={cx(
				'uiinput_txt',
				classArray,
				{ type_btn: useBtn || useBtnClear },
				{ type_unit: useUnit },
				{ alert: validation },
				{ dark: useDark },
			)}
			data-testid="wrap_input"
			style={style}
		>
			{useLabel && (
				<div className={cx('inner_label')}>
					<label
						htmlFor={id}
						className={cx('tit_input', { tRequired: required })}
						style={labelStyle}
					>
						{label}
					</label>
					{useSubLabel && <span className={cx('tit_sub_input')}> {subLabel}</span>}
				</div>
			)}
			{useLabelBlind && (
				<label
					htmlFor={id}
					className="blind"
				>
					{label}
				</label>
			)}
			<div className={cx(design, inputDesignClass, value?.toString().length && 'enter', placeholderEffect)}>
				<input
					type={getInputType()}
					id={id}
					name={name}
					value={
						(type === 'number' || type === 'customNumber') && useCommaFormat && !isFocused && value !== ''
							? numberWithCommas(value)
							: value
					}
					maxLength={maxLength}
					readOnly={readOnly}
					onChange={onChangeWrapper}
					onFocus={readOnly ? noFocus : internalOnFocus}
					onBlur={internalOnBlur}
					onKeyPress={onKeyPress}
					onKeyDown={onKeyDown}
					ref={ref}
					disabled={disabled}
					data-testid={testId}
					inputMode={inputMode}
					autoComplete={autoComplete}
					{...dataSet}
					className={cx(valueClass)}
				/>
				{placeholder && (
					/* eslint-disable-next-line */
					<div
						className={cx('placeholder_text')}
						role="button"
						onClick={() => onPlaceHolderFocus(id)}
					>
						{placeholder}
					</div>
				)}
				{useUnit && <span className={cx('unit', unitClass)}>{unitName}</span>}
				{useBtnClear && value && (
					<button
						type="button"
						className={cx('btn', 'btn_clear')}
						onClick={handleClear}
						{...dataSet}
					>
						<span>삭제</span>
					</button>
				)}
				{useBtn && (
					<button
						type="button"
						className={cx('btn', btnClass, { disabled: useBtnEmptyDisabled && btnDisabled })}
						onClick={onClick}
						disabled={btnDisabled}
						{...dataSet}
					>
						<span>{btnName}</span>
					</button>
				)}
			</div>
			{validation && (
				<>
					{validationText && (
						<p
							className={cx('noti_txt')}
							data-testid="validateText"
						>
							{validationText}
						</p>
					)}
					{validationTextAuto && (
						<p
							className={cx('noti_txt')}
							data-testid="validateText"
						>
							{label}
							{validationTextAuto} 입력해주세요.
						</p>
					)}
				</>
			)}
		</div>
	);
});

export const InputTextForStorybook = InputText;

export default memo(InputText);
